import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, ScrollView, Platform } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores, fuentes } from '../../../../styles/variablesCss';
import { BACKEND_URL } from '../../../../Config/Const';
import Storage from '../../../../utils/Storage';
import { useProducts } from '../../../../context/ProductContext';
import { useNavigation } from '@react-navigation/native';

const QueMasCompras = forwardRef((props, ref) => {
  const { autoFetch = true } = props; // Por defecto true para mantener compatibilidad
  const navigation = useNavigation();
  const { setProductos, setSearchText, setDepartmentId, setCategoryId, setSubcategoryId, 
    setDepartmentName, setCategoryName, setSubcategoryName } = useProducts();
  
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);

  const obtenerAuthYSucursal = async () => {
    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        return {
          mail: localStorage.getItem('mail') || '',
          codUsu: localStorage.getItem('codUsu') || '',
          suc: localStorage.getItem('sucursalId') || '4',
        };
      }
      return {
        mail: (await Storage.getItem('mail')) || '',
        codUsu: (await Storage.getItem('codUsu')) || '',
        suc: (await Storage.getItem('sucursalId')) || '4',
      };
    } catch (e) {
      return { mail: '', codUsu: '', suc: '4' };
    }
  };

  const fetchMasCompras = async () => {
    setLoading(true);
    setError(null);
    try {
      const { mail, codUsu, suc } = await obtenerAuthYSucursal();

      if (!mail || !codUsu) {
        setError('Iniciá sesión para ver esta sección.');
        setItems([]);
        return;
      }

      const params = new URLSearchParams();
      params.append('mail', mail);
      params.append('codUsu', codUsu);
      params.append('suc', suc);

      const url = `${BACKEND_URL}/rest/productos/mas-compras?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const text = await response.text();
      let data = null;
      try { data = JSON.parse(text); } catch {}

      const ds = data?.dsResultado || data?.response?.dsResultado?.dsResultado || null;
      const lista = Array.isArray(ds?.productos) ? ds.productos : Array.isArray(ds?.items) ? ds.items : [];

      const faltantes = Array.isArray(ds?.productosFaltantes)
        ? ds.productosFaltantes.map((p) => ({
            ...p,
            faltante: true,
            precio: p.precio ?? 0,
            precioSinImp: p.precioSinImp ?? 0,
            stock: p.stock ?? 0,
            maxStock: p.maxStock ?? 0,
            minStock: p.minStock ?? 1,
            oferta: p.oferta ?? '',
            cuotasSinInt: p.cuotasSinInt ?? 0,
            fotos: p.fotos ?? (p.foto ? [{ plu: p.plu, imagen: p.foto }] : []),
          }))
        : [];

      const combined = [...lista, ...faltantes];

      setItems(combined);

      // Limpiar contexto de búsqueda y categorías
      setSearchText('');
      setDepartmentId('0');
      setCategoryId('0');
      setSubcategoryId('0');
      setDepartmentName('');
      setCategoryName('');
      setSubcategoryName('');

      // Pasar productos al contexto
      setProductos(combined);

      // Navegar a /productos con parámetro masCompras=true
      if (Platform.OS === 'web' && window) {
        window.location.href = '/productos?masCompras=true';
      } else if (navigation) {
        navigation.navigate('Productos', { masCompras: true });
      }
    } catch (err) {
      setError('No pudimos cargar esta sección. Intentalo más tarde.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoFetch) {
      fetchMasCompras();
    }
  }, [autoFetch]);

  // Exponer fetchMasCompras al componente padre a través de ref
  useImperativeHandle(ref, () => ({
    fetchMasCompras,
  }));

  if (loading) {
    return (
      <View style={styles.center}> 
        <ActivityIndicator size="large" color={colores.rojo} />
        <Text style={styles.loadingText}>Cargando...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.center}>
        <MaterialCommunityIcons name="alert-circle-outline" size={40} color={colores.error} />
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }

  if (!items || items.length === 0) {
    return (
      <View style={styles.center}>
        <MaterialCommunityIcons name="playlist-remove" size={40} color={colores.grisClaro} />
        <Text style={styles.emptyText}>Aún no tenemos suficientes datos.</Text>
      </View>
    );
  }

  return (
    <Text style={styles.tituloSeccion}>Lo que más comprás</Text>
  );
});

const styles = StyleSheet.create({
  container: { width: '100%' },
  center: { alignItems: 'center', justifyContent: 'center', paddingVertical: 20 },
  tituloSeccion: { fontFamily: fuentes.bold, fontSize: 18, color: colores.rojo, marginBottom: 10 },
  loadingText: { marginTop: 10, color: colores.textoPrincipal, fontFamily: fuentes.regular },
  errorText: { marginTop: 8, color: colores.error, fontFamily: fuentes.regular },
  emptyText: { marginTop: 8, color: colores.texto, fontFamily: fuentes.regular },
  itemCard: { backgroundColor: colores.blanco, borderWidth: 1, borderColor: colores.borde, borderRadius: 6, padding: 12, marginBottom: 10 },
  itemRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 },
  itemLabel: { fontFamily: fuentes.semiBold, color: colores.textoPrincipal },
  itemValue: { fontFamily: fuentes.regular, color: colores.texto },
});

export default QueMasCompras;
